export const BERLIN_TIME_ZONE = 'Europe/Berlin';

export type BerlinDateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

export type BerlinFormFields = { date: string; time: string };

const twoDigits = (value: number): string => String(value).padStart(2, '0');

const partsFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: BERLIN_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

export const getBerlinDateParts = (timestamp: number): BerlinDateParts => {
  const values = new Map(partsFormatter.formatToParts(timestamp).map((part) => [part.type, Number(part.value)]));
  return {
    year: values.get('year') ?? 0,
    month: values.get('month') ?? 0,
    day: values.get('day') ?? 0,
    hour: values.get('hour') ?? 0,
    minute: values.get('minute') ?? 0,
    second: values.get('second') ?? 0,
  };
};

export const getBerlinDateKey = (timestamp: number): string => {
  const { year, month, day } = getBerlinDateParts(timestamp);
  return `${year}-${twoDigits(month)}-${twoDigits(day)}`;
};

const formatterCache = new Map<string, Intl.DateTimeFormat>();
const berlinFormatter = (locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  const key = JSON.stringify([locale, options]);
  const cached = formatterCache.get(key);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat(locale, { ...options, timeZone: BERLIN_TIME_ZONE });
  formatterCache.set(key, formatter);
  return formatter;
};

export const formatBerlinDateWithOptions = (timestamp: number, options: Intl.DateTimeFormatOptions, locale: string = 'de-DE'): string =>
  berlinFormatter(locale, options).format(timestamp);

export const formatBerlinDate = (timestamp: number, locale: string = 'de-DE', options?: Intl.DateTimeFormatOptions): string =>
  formatBerlinDateWithOptions(timestamp, options ?? { day: '2-digit', month: '2-digit', year: 'numeric' }, locale);

export const formatBerlinTime = (timestamp: number, locale: string = 'de-DE'): string =>
  formatBerlinDateWithOptions(timestamp, { hour: '2-digit', minute: '2-digit' }, locale);

export const formatBerlinDateTime = (timestamp: number, locale: string = 'de-DE'): string =>
  formatBerlinDateWithOptions(timestamp, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }, locale);

export const formatBerlinWeekday = (timestamp: number, locale: string = 'de-DE', width: 'long' | 'short' = 'long'): string =>
  formatBerlinDateWithOptions(timestamp, { weekday: width }, locale);

export const formatBerlinDutyRange = (startTimestamp: number, endTimestamp: number, locale: string = 'de-DE'): string => {
  const startDate = formatBerlinDateWithOptions(startTimestamp, { day: '2-digit', month: '2-digit', year: 'numeric' }, locale);
  const endDate = formatBerlinDateWithOptions(endTimestamp, { day: '2-digit', month: '2-digit', year: 'numeric' }, locale);
  const startTime = formatBerlinTime(startTimestamp, locale);
  const endTime = formatBerlinTime(endTimestamp, locale);
  return startDate === endDate ? `${startDate}, ${startTime}–${endTime}` : `${startDate}, ${startTime} – ${endDate}, ${endTime}`;
};

export const isBerlinToday = (timestamp: number, referenceTimestamp: number = Date.now()): boolean =>
  getBerlinDateKey(timestamp) === getBerlinDateKey(referenceTimestamp);

const dateKeyToUtcOrdinal = (dateKey: string): number => {
  const [year, month, day] = dateKey.split('-').map(Number);
  return Date.UTC(year, month - 1, day) / 86_400_000;
};

export const getBerlinCalendarDayDifference = (fromTimestamp: number, toTimestamp: number): number =>
  dateKeyToUtcOrdinal(getBerlinDateKey(toTimestamp)) - dateKeyToUtcOrdinal(getBerlinDateKey(fromTimestamp));

export const epochToBerlinFormFields = (timestamp: number): BerlinFormFields => {
  const parts = getBerlinDateParts(timestamp);
  return { date: `${parts.year}-${twoDigits(parts.month)}-${twoDigits(parts.day)}`, time: `${twoDigits(parts.hour)}:${twoDigits(parts.minute)}` };
};

export const epochToBerlinDateTimeLocalValue = (timestamp: number): string => {
  const fields = epochToBerlinFormFields(timestamp);
  return `${fields.date}T${fields.time}`;
};

const parseDateAndTime = (date: string, time: string): BerlinDateParts | null => {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const timeMatch = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time);
  if (!dateMatch || !timeMatch) return null;
  const parts = {
    year: Number(dateMatch[1]),
    month: Number(dateMatch[2]),
    day: Number(dateMatch[3]),
    hour: Number(timeMatch[1]),
    minute: Number(timeMatch[2]),
    second: Number(timeMatch[3] ?? 0),
  };
  if (parts.month < 1 || parts.month > 12 || parts.day < 1 || parts.day > 31 || parts.hour > 23 || parts.minute > 59 || parts.second > 59) return null;
  return parts;
};

const sameParts = (left: BerlinDateParts, right: BerlinDateParts): boolean =>
  left.year === right.year && left.month === right.month && left.day === right.day && left.hour === right.hour && left.minute === right.minute && left.second === right.second;

/** Converts a German civil time to an instant. Repeated autumn times select the earlier instant. */
export const berlinWallTimeToEpoch = (date: string, time: string, preferredTimestamp?: number): number | null => {
  const desired = parseDateAndTime(date, time);
  if (!desired) return null;
  const nominalUtc = Date.UTC(desired.year, desired.month - 1, desired.day, desired.hour, desired.minute, desired.second);
  const matches: number[] = [];
  for (let offsetMinutes = 180; offsetMinutes >= 0; offsetMinutes -= 30) {
    const candidate = nominalUtc - offsetMinutes * 60_000;
    if (sameParts(getBerlinDateParts(candidate), desired)) matches.push(candidate);
  }
  if (matches.length === 0) return null;
  if (preferredTimestamp !== undefined && matches.includes(preferredTimestamp)) return preferredTimestamp;
  return Math.min(...matches);
};

const shiftCivilDate = (dateKey: string, days: number): string => {
  const [year, month, day] = dateKey.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return `${shifted.getUTCFullYear()}-${twoDigits(shifted.getUTCMonth() + 1)}-${twoDigits(shifted.getUTCDate())}`;
};

const startOfBerlinDateKey = (dateKey: string): number => berlinWallTimeToEpoch(dateKey, '00:00') ?? Number.NaN;

export const startOfBerlinDay = (timestamp: number): number => startOfBerlinDateKey(getBerlinDateKey(timestamp));
export const endOfBerlinDay = (timestamp: number): number => startOfBerlinDateKey(shiftCivilDate(getBerlinDateKey(timestamp), 1)) - 1;

export const startOfBerlinMonth = (timestamp: number): number => {
  const { year, month } = getBerlinDateParts(timestamp);
  return startOfBerlinDateKey(`${year}-${twoDigits(month)}-01`);
};

export const endOfBerlinMonth = (timestamp: number): number => {
  const { year, month } = getBerlinDateParts(timestamp);
  const nextMonth = new Date(Date.UTC(year, month, 1));
  return startOfBerlinDateKey(`${nextMonth.getUTCFullYear()}-${twoDigits(nextMonth.getUTCMonth() + 1)}-01`) - 1;
};

export const startOfBerlinYear = (timestamp: number): number => startOfBerlinDateKey(`${getBerlinDateParts(timestamp).year}-01-01`);
export const endOfBerlinYear = (timestamp: number): number => startOfBerlinDateKey(`${getBerlinDateParts(timestamp).year + 1}-01-01`) - 1;

export const epochToBerlinWallClockDate = (timestamp: number): Date => {
  const parts = getBerlinDateParts(timestamp);
  return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second, new Date(timestamp).getUTCMilliseconds());
};

/** Date-only fields are stored as UTC midnight to keep their YYYY-MM-DD stable. */
export const dateOnlyTimestampToKey = (timestamp: number): string => {
  const date = new Date(timestamp);
  // Older records were stored at Berlin midnight. Preserve their civil date
  // while treating current UTC-midnight records as timezone-independent.
  return date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0 && date.getUTCMilliseconds() === 0
    ? date.toISOString().slice(0, 10)
    : getBerlinDateKey(timestamp);
};
export const dateOnlyKeyToTimestamp = (dateKey: string): number | null => {
  const parts = parseDateAndTime(dateKey, '00:00');
  if (!parts) return null;
  const timestamp = Date.UTC(parts.year, parts.month - 1, parts.day);
  return dateOnlyTimestampToKey(timestamp) === dateKey ? timestamp : null;
};
export const dateOnlyTimestampToPickerDate = (timestamp: number): Date => {
  const [year, month, day] = dateOnlyTimestampToKey(timestamp).split('-').map(Number);
  return new Date(year, month - 1, day);
};
export const pickerDateToDateOnlyTimestamp = (date: Date): number => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
export const dateKeyToBerlinReference = (dateKey: string): number => {
  const [year, month, day] = dateKey.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
};
export const pickerDateToBerlinReference = pickerDateToDateOnlyTimestamp;
export const pickerDateAndTimeToBerlinEpoch = (date: Date | undefined, time?: string): number | null => {
  if (!date) return null;
  const effectiveTime = time ?? `${twoDigits(date.getHours())}:${twoDigits(date.getMinutes())}`;
  return berlinWallTimeToEpoch(`${date.getFullYear()}-${twoDigits(date.getMonth() + 1)}-${twoDigits(date.getDate())}`, effectiveTime);
};
export const formatDateOnlyTimestamp = (timestamp: number, locale: string = 'de-DE'): string =>
  new Intl.DateTimeFormat(locale, { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric' }).format(
    dateOnlyKeyToTimestamp(dateOnlyTimestampToKey(timestamp)) ?? timestamp
  );
export const isDateOnlyExpired = (timestamp: number, referenceTimestamp: number = Date.now()): boolean =>
  dateOnlyTimestampToKey(timestamp) < getBerlinDateKey(referenceTimestamp);
export const getDateOnlyCalendarDayDifference = (timestamp: number, referenceTimestamp: number = Date.now()): number =>
  dateKeyToUtcOrdinal(dateOnlyTimestampToKey(timestamp)) - dateKeyToUtcOrdinal(getBerlinDateKey(referenceTimestamp));
